import React from 'react';
import { useSelector } from 'react-redux';

import { FlatButton } from '@travel/ui';
import { cx } from '@travel/utils/src/classNames';

import AmountInput from 'favoriteList/components/SearchForm/components/AmountInput';
import DateRow from 'favoriteList/components/SearchForm/components/DateRow';
import Form from 'favoriteList/components/SearchForm/components/Form';
import labels from 'favoriteList/components/SearchForm/constants/labels';
import type { SearchFormHook } from 'favoriteList/components/SearchForm/hooks/useSearchForm';
import {
  getIsProviderFetching,
  getProviderDataCount,
} from 'favoriteList/store/providerPlan/selectors';

import type { URLSearchQuery } from 'SearchForm-Types';

import styles from './searchFormContents.module.scss';

export type Props = {
  className?: string;
  /** String of path to be a destination when submit the form */
  formAction: string;
  /** Object of search query */
  query?: URLSearchQuery;
  /** form object from parent (to use the same form object for PC component) */
  form: SearchFormHook;
  /** List of holidays */
  holidays?: string[];
  /** Form values that are not date or amount input (e.g. hotel numbers, other necessary values) */
  commonFormValues: React.ReactNode;
  /** Flag for if searching domestic or overseas hotels */
  isDomestic: boolean;
};

function SearchFormContents(props: Props) {
  const { className, formAction, form, holidays, commonFormValues, isDomestic } = props;
  const isProvideFetching = useSelector(getIsProviderFetching);
  const providerDataCount = useSelector(getProviderDataCount);

  const isSearchEnabled =
    !!form.travel.startDate && !!form.travel.endDate && !isProvideFetching && providerDataCount > 0;

  return (
    <Form
      id="search"
      role="search"
      method="get"
      action={formAction}
      className={cx(styles.wrapper, className)}
      onSubmit={({ query }) => {
        if (isSearchEnabled) {
          window.location.assign(form.submitUrl + '?' + query);
        }
      }}
    >
      <div className={styles.content}>
        <DateRow
          className={cx(styles.dateRow, styles.borderButton, styles.spacing)}
          startDate={form.travel.startDate}
          endDate={form.travel.endDate}
          onDateChanged={form.travel.onChange}
          holidays={holidays}
        />

        <AmountInput
          className={cx(styles.placeInput, styles.borderButton, styles.sidePadding)}
          selectedCapacity={form.travel.capacity}
          isDomestic={isDomestic}
        />
      </div>
      <div className={styles.submitButtonWrapper}>
        <FlatButton
          type={'submit'}
          className={styles.submitButton}
          data-testId="search-content-submit"
          isDisabled={!isSearchEnabled}
        >
          {labels.searchVacancy}
        </FlatButton>
      </div>
      {commonFormValues}
    </Form>
  );
}

export default SearchFormContents;
